import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { isCompleteName } from '../../../shared/utils/names';
import { runGroupDeletion, purgeGroupProofs } from '../../../shared/utils/groupDeletion';
import { MESSAGING_CONSENT_VERSION } from '../../onboarding/constants/policyVersions';
import ManageMembersModal from './ManageMembersModal';

const STATUS_STYLES = {
  active: 'bg-success-100 text-success-800',
  paused: 'bg-warning-100 text-warning-800',
  completed: 'bg-primary-100 text-primary-800',
};

const FREQ_LABELS = { weekly: 'Weekly', 'bi-weekly': 'Bi-weekly', monthly: 'Monthly' };

const GroupSettingsModal = ({ group, onClose, onGroupUpdated, onGroupArchived, onGroupRemoved }) => {
  const [groupName, setGroupName] = useState(group.group_name);
  const [feesEnabled, setFeesEnabled] = useState(group.fees_enabled === true);
  const [lateFeeAmount, setLateFeeAmount] = useState(
    group.late_fee_amount != null ? String(group.late_fee_amount) : ''
  );
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  // Money-flow model (Stage 24 / Phase C1). Editable only when no cycle is live.
  const [moneyFlowModel, setMoneyFlowModel] = useState(group.money_flow_model || 'through_admin');
  const [activeCycle, setActiveCycle] = useState(group.current_round_id != null);
  // Admin's participating member identity (Stage 49) — links the group's admin role to
  // the member row that is them, so admin-directed alerts reach their member WhatsApp.
  const [adminMembers, setAdminMembers] = useState([]);   // {id, name, whatsapp_number, hasV2}
  const [selectedAdminMember, setSelectedAdminMember] = useState(
    group.admin_member_id != null ? String(group.admin_member_id) : ''
  );
  const [savingAdminMember, setSavingAdminMember] = useState(false);
  const [adminMemberError, setAdminMemberError] = useState(null);
  const [adminMemberSaved, setAdminMemberSaved] = useState(false);
  // Organizer (admin) name — stored in auth user_metadata.full_name (no public
  // profiles table). Lets an admin complete a partial name; required in full for the
  // FHA statement, where the organizer is the attesting party.
  const [organizerName, setOrganizerName] = useState('');
  const [organizerLoaded, setOrganizerLoaded] = useState(false);
  // Delete/archive (Stage 39).
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Confirm the active-cycle state from the source of truth (the flag prop may be
  // absent depending on the select). current_round_id != null == a cycle is live.
  useEffect(() => {
    let on = true;
    (async () => {
      const { data } = await supabaseClient.from('groups').select('current_round_id').eq('id', group.id).single();
      if (on && data) setActiveCycle(data.current_round_id != null);
    })();
    return () => { on = false; };
  }, [group.id]);

  // Load the group's members + which have current-version WhatsApp consent, for the
  // "Your participating member" picker (admin-scoped reads; no new RPC).
  useEffect(() => {
    let on = true;
    (async () => {
      const [{ data: mem }, { data: cons }] = await Promise.all([
        supabaseClient.from('members').select('id, name, whatsapp_number, status').eq('group_id', group.id),
        supabaseClient.from('member_consents').select('member_db_id')
          .eq('group_id', group.id).eq('messaging_consent_version', MESSAGING_CONSENT_VERSION),
      ]);
      if (!on) return;
      const consented = new Set((cons || []).map(c => String(c.member_db_id)));
      setAdminMembers((mem || [])
        .filter(m => m.status !== 'removed')
        .map(m => ({ id: m.id, name: m.name, whatsapp_number: m.whatsapp_number, hasV2: consented.has(String(m.id)) })));
    })();
    return () => { on = false; };
  }, [group.id]);

  const handleSaveAdminMember = async () => {
    setSavingAdminMember(true); setAdminMemberError(null); setAdminMemberSaved(false);
    try {
      const memberId = selectedAdminMember ? parseInt(selectedAdminMember, 10) : null;
      const { error } = await supabaseClient.rpc('set_admin_member', { p_group_id: group.id, p_member_db_id: memberId });
      if (error) throw error;
      setAdminMemberSaved(true);
      onGroupUpdated?.({ ...group, admin_member_id: memberId });
    } catch (e) {
      setAdminMemberError(e.message || 'Could not save.');
    } finally {
      setSavingAdminMember(false);
    }
  };

  // Load the organizer's current name from auth metadata (self-readable).
  useEffect(() => {
    let on = true;
    (async () => {
      const { data: { user } } = await supabaseClient.auth.getUser();
      if (on) {
        setOrganizerName(user?.user_metadata?.full_name || '');
        setOrganizerLoaded(true);
      }
    })();
    return () => { on = false; };
  }, []);

  const inviteLink = `${window.location.origin}/join/${group.id}`;

  // Audit: invite link viewed when modal opens
  useEffect(() => {
    const logView = async () => {
      try {
        const { data: { user } } = await supabaseClient.auth.getUser();
        await supabaseClient.from('audit_logs').insert({
          event_category: 'group_management',
          event_type: 'invite_link_viewed',
          actor_id: user?.id ?? null,
          actor_role: 'admin',
          new_value: { group_id: group.id, invite_link: inviteLink },
          risk_level: 'low',
          flagged: false,
          timestamp: new Date().toISOString(),
        });
      } catch {}
    };
    logView();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  // Primary delete path: empty group -> hard-delete now; group with history -> archive
  // (recoverable 30 days). Permanent deletion of an archived group is done from the
  // dashboard's Archived view (DeleteGroupModal: export-first + type-to-confirm).
  const handleDelete = async () => {
    setDeleting(true); setDeleteError(null);
    try {
      const result = await runGroupDeletion(group.id, 'delete');
      if (result.action === 'hard_deleted_empty') {
        await purgeGroupProofs(result); // empty -> no proofs, but harmless/consistent
        onGroupRemoved?.(group.id);
      } else {
        onGroupArchived?.(group.id); // archived -> drops out of the active view
      }
      onClose();
    } catch (err) {
      setDeleteError(err.message || 'Could not delete the group.');
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    const trimmed = groupName.trim();
    if (!trimmed) { setSaveError('Group name is required.'); return; }

    // Late fee amount is optional (a suggested default); if provided it must be > 0.
    let normalizedAmount = null;
    const raw = lateFeeAmount.trim();
    if (raw !== '') {
      const v = parseFloat(raw);
      if (isNaN(v) || v <= 0) { setSaveError('Late fee amount must be greater than zero.'); return; }
      normalizedAmount = v;
    }

    // Organizer name: if provided it must be a complete legal name (first + last).
    const trimmedOrganizer = organizerName.trim();
    if (trimmedOrganizer && !isCompleteName(trimmedOrganizer)) {
      setSaveError('Enter your first and last name (your full legal name as organizer).');
      return;
    }

    const nameChanged = trimmed !== group.group_name;
    const feesChanged =
      feesEnabled !== (group.fees_enabled === true) ||
      normalizedAmount !== (group.late_fee_amount ?? null);
    const modelChanged = moneyFlowModel !== (group.money_flow_model || 'through_admin');
    if (modelChanged && activeCycle) {
      setSaveError('The money-flow model can only be changed before the first cycle or between cycles.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();

      // Update the organizer's own name in auth metadata if it changed.
      const organizerChanged = organizerLoaded && trimmedOrganizer &&
        trimmedOrganizer !== (user?.user_metadata?.full_name || '');
      if (organizerChanged) {
        const { error: nameErr } = await supabaseClient.auth.updateUser({ data: { full_name: trimmedOrganizer } });
        if (nameErr) throw nameErr;
      }

      const groupChanged = nameChanged || feesChanged || modelChanged;
      if (!groupChanged && !organizerChanged) { onClose(); return; }

      // Only touch the group row (+ audit) when a GROUP field actually changed, so an
      // organizer-name-only save doesn't write a no-op group_settings_updated row.
      let updated = null;
      if (groupChanged) {
        const { data, error } = await supabaseClient
          .from('groups')
          // fees_enabled / late_fee_amount are additive settings columns; this does
          // NOT touch contribution/pot logic (the engine never reads them).
          .update({
            group_name: trimmed,
            fees_enabled: feesEnabled,
            late_fee_amount: normalizedAmount,
            ...(modelChanged ? { money_flow_model: moneyFlowModel } : {}),
          })
          .eq('id', group.id)
          .select()
          .single();
        if (error) throw error;
        updated = data;

        await supabaseClient.from('audit_logs').insert({
          event_category: 'group_management',
          event_type: 'group_settings_updated',
          actor_id: user?.id ?? null,
          actor_role: 'admin',
          old_value: { group_name: group.group_name, fees_enabled: group.fees_enabled === true, late_fee_amount: group.late_fee_amount ?? null },
          new_value: { group_name: trimmed, fees_enabled: feesEnabled, late_fee_amount: normalizedAmount },
          risk_level: 'low',
          flagged: false,
          timestamp: new Date().toISOString(),
          metadata: { group_id: group.id },
        });
      }

      if (updated) onGroupUpdated(updated);
      onClose();
    } catch (err) {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-100 rounded-lg w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-neutral-900">Group Settings</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-5 flex-1 overflow-y-auto">
          {/* Group Name (editable) */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Group Name</label>
            <input
              type="text"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Organizer (your) name — stored in auth metadata; needed in full for the
              FHA statement, where you are the attesting organizer. */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Your name (organizer)</label>
            <input
              type="text"
              value={organizerName}
              onChange={e => setOrganizerName(e.target.value)}
              placeholder="First and last name"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
            {organizerLoaded && organizerName.trim() && !isCompleteName(organizerName) && (
              <p className="mt-1 text-xs text-warning-700">
                Enter your full legal name (first and last) — required on official savings-club documents.
              </p>
            )}
            <p className="mt-1 text-xs text-neutral-400">
              Your full legal name as the group organizer. This names you as the attesting party on member records.
            </p>
          </div>

          {/* Money-flow model (Stage 24 / Phase C1) — editable only between cycles */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">How members pay</label>
            {activeCycle ? (
              <>
                <p className="text-sm font-medium text-neutral-900">
                  {moneyFlowModel === 'direct_to_collector' ? 'Members pay the collector directly' : 'Members pay the admin'}
                </p>
                <p className="mt-1 text-xs text-warning-700">Locked while a cycle is running — change it between cycles.</p>
              </>
            ) : (
              <>
                <select
                  value={moneyFlowModel}
                  onChange={e => setMoneyFlowModel(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                >
                  <option value="through_admin">Members pay the admin</option>
                  <option value="direct_to_collector">Members pay the collector directly</option>
                </select>
                <p className="mt-1 text-xs text-neutral-500">Can be changed now (no cycle is running).</p>
              </>
            )}
          </div>

          {/* Read-only details */}
          <div className="bg-neutral-50 rounded-lg p-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-500">Contribution per hand</span>
              <span className="font-medium text-neutral-900">
                ${parseFloat(group.contribution_amount_per_hand || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Frequency</span>
              <span className="font-medium text-neutral-900">
                {FREQ_LABELS[group.contribution_frequency] || group.contribution_frequency}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Status</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[group.status] || STATUS_STYLES.active}`}>
                {group.status || 'active'}
              </span>
            </div>
            {group.start_date && (
              <div className="flex justify-between">
                <span className="text-neutral-500">Start date</span>
                <span className="font-medium text-neutral-900">
                  {new Date(group.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            )}
          </div>

          {/* Late Fees (per-group, off by default). Separate member-level debt —
              never enters the pot/rotation. */}
          <div>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-neutral-700">Late fees</span>
              <input
                type="checkbox"
                checked={feesEnabled}
                onChange={e => setFeesEnabled(e.target.checked)}
                className="h-5 w-5"
              />
            </label>
            <p className="mt-1 text-xs text-neutral-400">
              Charge members a penalty for late contributions. Tracked separately from the pot — never affects rotation or payouts.
            </p>
            {feesEnabled && (
              <div className="mt-3">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Default late fee amount (optional)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-neutral-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={lateFeeAmount}
                    onChange={e => setLateFeeAmount(e.target.value)}
                    placeholder="20.00"
                    className="w-full pl-7 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <p className="mt-1 text-xs text-neutral-400">Pre-fills the "Add Late Fee" amount; editable per fee.</p>
              </div>
            )}
          </div>

          {/* Group Invite Link */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Group Invite Link
              <span className="ml-2 text-xs font-normal text-neutral-400">Never expires</span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={inviteLink}
                onClick={e => e.target.select()}
                className="flex-1 px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-neutral-50 text-neutral-600 truncate"
              />
              <button
                onClick={handleCopy}
                className="flex-shrink-0 px-3 py-2 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 text-neutral-700 whitespace-nowrap"
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-1 text-xs text-neutral-400">
              Share this link for anyone to self-register and join this group.
              {/* TODO: Add regenerate button when group-level token system is implemented */}
            </p>
          </div>

          {/* Your participating member (Stage 49) — link the admin's member identity so
              admin alerts (e.g. payment submissions) reach them by WhatsApp. FRANNIE DRAFT. */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Your participating member</label>
            <p className="text-xs text-neutral-500">
              Are you also a member of this circle? Link your member profile here so your admin alerts
              can reach you by WhatsApp, on the number from that profile.
            </p>
            <p className="text-xs text-neutral-400 mb-2">
              (Your admin account and member account are separate, so this connects them.)
            </p>
            <select
              value={selectedAdminMember}
              onChange={e => { setSelectedAdminMember(e.target.value); setAdminMemberSaved(false); }}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Not linked</option>
              {adminMembers.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
            </select>
            {(() => {
              const sel = adminMembers.find(m => String(m.id) === String(selectedAdminMember));
              if (!sel || (sel.whatsapp_number && sel.hasV2)) return null;
              return (
                <p className="mt-1 text-xs text-warning-700">
                  Heads up: WhatsApp can't reach this profile yet. Either there's no WhatsApp number on it,
                  or they haven't agreed to WhatsApp messaging. Your in-app alerts still work.
                </p>
              );
            })()}
            {adminMemberError && <p className="mt-1 text-xs text-error-600">{adminMemberError}</p>}
            <button
              type="button"
              onClick={handleSaveAdminMember}
              disabled={savingAdminMember}
              className="mt-2 px-4 py-2 border border-primary-600 text-primary-700 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {savingAdminMember ? 'Saving…' : adminMemberSaved ? 'Saved ✓' : 'Save'}
            </button>
          </div>

          {/* Manage Members shortcut */}
          <button
            onClick={() => setShowMembers(true)}
            className="w-full flex items-center justify-between px-4 py-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-left"
          >
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-neutral-900">Manage Members</p>
                <p className="text-xs text-neutral-500">View, edit, reassign hands, or remove members</p>
              </div>
            </div>
            <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {saveError && <p className="text-sm text-error-600">{saveError}</p>}

          {/* Danger zone — delete/archive (Stage 39). FRANNIE DRAFT copy. */}
          <div className="pt-4 border-t border-error-200">
            <h4 className="text-sm font-semibold text-error-700 mb-1">Delete this group</h4>
            {!confirmDelete ? (
              <>
                <p className="text-xs text-neutral-500 mb-2">
                  A group with any records is archived first (recoverable for 30 days), then can be
                  permanently deleted. An empty group is deleted right away.
                </p>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="px-4 py-2 border border-error-300 text-error-700 rounded-lg text-sm font-medium hover:bg-error-50"
                >
                  Delete group
                </button>
              </>
            ) : (
              <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                <p className="text-xs text-error-800 mb-2">
                  Delete <strong>{group.group_name}</strong>? If it has records it will be archived
                  (recoverable for 30 days); if it is empty it will be permanently deleted now.
                </p>
                {deleteError && <p className="text-xs text-error-700 mb-2">{deleteError}</p>}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setConfirmDelete(false); setDeleteError(null); }}
                    disabled={deleting}
                    className="px-3 py-1.5 border border-neutral-300 rounded-lg text-xs font-medium hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-3 py-1.5 bg-error-600 text-neutral-50 rounded-lg text-xs font-semibold hover:bg-error-700 disabled:opacity-50"
                  >
                    {deleting ? 'Working…' : 'Confirm delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex space-x-3 px-6 py-4 border-t border-neutral-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !groupName.trim()}
            className="flex-1 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* ManageMembersModal — opened from within GroupSettingsModal */}
      {showMembers && (
        <ManageMembersModal
          group={group}
          onClose={() => setShowMembers(false)}
        />
      )}
    </div>
  );
};

export default GroupSettingsModal;
